import { z } from 'zod';
import { documentGenerationEngineSchema, documentGenerationKindSchema } from './document-generation';

// ---------------------------------------------------------------------------
// Modèle de document par blocs (moteur `blocks`, ADR-2026-10-07).
//
// Source de vérité unique d'un document généré : le LLM produit ce modèle
// (plan puis sections en parallèle), un rendu déterministe en tire HTML, PDF,
// DOCX, PPTX ou XLSX. Le miroir Pydantic vit dans
// `backend-codegen/skills/inersio_docgen/render/model.py` et doit rester
// strictement aligné (mêmes champs, mêmes bornes).
//
// Texte inline (`InlineText`) : chaîne avec un balisage minimal, interprété par
// le rendu et jamais affiché tel quel :
//   **gras**, *italique*, [libellé](https://url), [S1] (clé de source),
//   échappements \* \\ \[ \] pour un caractère littéral (émis par l'éditeur du canvas).
// Tout autre caractère est du texte littéral.
// ---------------------------------------------------------------------------

export const DOCUMENT_MODEL_VERSION = 1 as const;

const inlineTextSchema = z.string().trim().min(1).max(4_000);
const shortTextSchema = z.string().trim().min(1).max(200);
export const documentModelIdSchema = z.string().trim().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, {
  error: 'Identifiant de bloc invalide.',
});
const idSchema = documentModelIdSchema;

export const documentThemePresetSchema = z.enum([
  'corporate',
  'modern',
  'executive',
  'institutional',
  'minimal',
  'editorial',
], { error: 'Thème de document non supporté.' });

const hexColorSchema = z.string().regex(/^[0-9A-F]{6}$/i, {
  error: 'La couleur doit être un code hexadécimal à six caractères sans #.',
});

export const documentThemeSchema = z.object({
  /** Couleurs imposées par l'utilisateur ou la marque ; sinon celles du preset. */
  accentColor: hexColorSchema.optional(),
  density: z.enum(['compact', 'balanced', 'spacious']).default('balanced'),
  preset: documentThemePresetSchema,
  primaryColor: hexColorSchema.optional(),
}).strict();

export const documentChartTypeSchema = z.enum([
  'bar',
  'horizontal_bar',
  'stacked_bar',
  'line',
  'area',
  'pie',
  'donut',
]);

export const documentChartSchema = z.object({
  caption: inlineTextSchema.optional(),
  categories: z.array(shortTextSchema).min(1).max(24),
  chartType: documentChartTypeSchema,
  series: z.array(z.object({
    name: shortTextSchema,
    values: z.array(z.number().finite()).min(1).max(24),
  }).strict()).min(1).max(6),
  sourceKey: z.string().trim().min(1).max(20).optional(),
  title: shortTextSchema,
  /** Unité affichée sur l'axe ou dans les étiquettes (« M€ », « % »…). */
  unit: z.string().trim().min(1).max(20).optional(),
}).strict().refine(
  (chart) => chart.series.every((serie) => serie.values.length === chart.categories.length),
  { error: 'Chaque série doit avoir autant de valeurs que de catégories.', path: ['series'] },
);

export const documentTableColumnSchema = z.object({
  align: z.enum(['left', 'center', 'right']).optional(),
  format: z.enum(['text', 'number', 'integer', 'currency', 'percent', 'date']).default('text'),
  header: shortTextSchema,
}).strict();

const blockBase = { id: idSchema };

const tableBlockSchema = z.object({
  ...blockBase,
  caption: inlineTextSchema.optional(),
  columns: z.array(documentTableColumnSchema).min(1).max(8),
  /** Cellules en texte inline ; les nombres sont formatés par le rendu selon la colonne. */
  rows: z.array(z.array(z.string().max(400))).min(1).max(60),
  sourceKey: z.string().trim().min(1).max(20).optional(),
  totalRow: z.boolean().default(false),
  type: z.literal('table'),
}).strict();

export const documentBlockSchema = z.discriminatedUnion('type', [
  z.object({ ...blockBase, level: z.union([z.literal(2), z.literal(3)]), text: shortTextSchema, type: z.literal('heading') }).strict(),
  z.object({ ...blockBase, style: z.enum(['normal', 'lead']).default('normal'), text: inlineTextSchema, type: z.literal('paragraph') }).strict(),
  z.object({
    ...blockBase,
    items: z.array(z.object({
      children: z.array(inlineTextSchema).max(8).optional(),
      text: inlineTextSchema,
    }).strict()).min(1).max(20),
    style: z.enum(['bullet', 'numbered']),
    type: z.literal('list'),
  }).strict(),
  tableBlockSchema,
  z.object({
    ...blockBase,
    items: z.array(z.object({
      delta: z.string().trim().min(1).max(30).optional(),
      label: z.string().trim().min(1).max(60),
      note: z.string().trim().min(1).max(120).optional(),
      trend: z.enum(['up', 'down', 'flat']).optional(),
      value: z.string().trim().min(1).max(30),
    }).strict()).min(2).max(6),
    type: z.literal('kpis'),
  }).strict(),
  z.object({
    ...blockBase,
    text: inlineTextSchema,
    title: shortTextSchema.optional(),
    type: z.literal('callout'),
    variant: z.enum(['info', 'success', 'warning', 'key']),
  }).strict(),
  z.object({ ...blockBase, attribution: shortTextSchema.optional(), text: inlineTextSchema, type: z.literal('quote') }).strict(),
  z.object({ ...blockBase, chart: documentChartSchema, type: z.literal('chart') }).strict(),
  z.object({
    ...blockBase,
    items: z.array(z.object({
      date: z.string().trim().min(1).max(40),
      text: inlineTextSchema.optional(),
      title: shortTextSchema,
    }).strict()).min(2).max(12),
    type: z.literal('timeline'),
  }).strict(),
  z.object({
    ...blockBase,
    columns: z.array(z.object({
      points: z.array(inlineTextSchema).min(1).max(8),
      title: shortTextSchema,
    }).strict()).min(2).max(4),
    type: z.literal('comparison'),
  }).strict(),
  z.object({
    ...blockBase,
    opportunities: z.array(inlineTextSchema).min(1).max(6),
    strengths: z.array(inlineTextSchema).min(1).max(6),
    threats: z.array(inlineTextSchema).min(1).max(6),
    type: z.literal('swot'),
    weaknesses: z.array(inlineTextSchema).min(1).max(6),
  }).strict(),
  z.object({
    ...blockBase,
    items: z.array(z.object({ text: inlineTextSchema.optional(), title: shortTextSchema }).strict()).min(2).max(8),
    type: z.literal('steps'),
  }).strict(),
  // Fiches acteurs (2026-10-08) : un acteur, une offre ou une option présenté avec ce qu'on en sait.
  z.object({
    ...blockBase,
    items: z.array(z.object({
      metric: z.object({
        label: z.string().trim().min(1).max(60),
        value: z.string().trim().min(1).max(30),
      }).strict().optional(),
      subtitle: shortTextSchema.optional(),
      tags: z.array(z.string().trim().min(1).max(40)).max(6).optional(),
      text: inlineTextSchema.optional(),
      title: shortTextSchema,
    }).strict()).min(2).max(8),
    type: z.literal('cards'),
  }).strict(),
  // Chiffre clé en exergue : un nombre marquant et ce qu'il signifie.
  z.object({
    ...blockBase,
    sourceKey: z.string().trim().min(1).max(20).optional(),
    text: inlineTextSchema,
    type: z.literal('highlight'),
    value: z.string().trim().min(1).max(30),
  }).strict(),
  z.object({
    ...blockBase,
    alt: shortTextSchema,
    caption: inlineTextSchema.optional(),
    /** Image encodée en base64 (PNG, JPEG ou SVG). Réservé à la voie script : le moteur blocks n'en produit pas. */
    dataBase64: z.string().min(1).max(8_000_000),
    mediaType: z.enum(['image/png', 'image/jpeg', 'image/svg+xml']),
    type: z.literal('image'),
  }).strict(),
  z.object({ ...blockBase, type: z.literal('divider') }).strict(),
  z.object({ ...blockBase, type: z.literal('page_break') }).strict(),
]).superRefine((block, ctx) => {
  // Les refinements ne peuvent pas vivre dans un membre d'union discriminée.
  if (block.type === 'table' && !block.rows.every((row) => row.length === block.columns.length)) {
    ctx.addIssue({ code: 'custom', message: 'Chaque ligne doit avoir autant de cellules que de colonnes.', path: ['rows'] });
  }
});

export const documentBlockTypeSchema = z.enum([
  'heading', 'paragraph', 'list', 'table', 'kpis', 'callout', 'quote', 'chart',
  'timeline', 'comparison', 'swot', 'steps', 'cards', 'highlight', 'image', 'divider', 'page_break',
]);

export const documentSectionStatusSchema = z.enum(['pending', 'ready', 'failed']);

export const documentSectionSchema = z.object({
  blocks: z.array(documentBlockSchema).max(60),
  id: idSchema,
  /** Rôle éditorial issu du plan ; guide la génération et la régénération ciblée. */
  purpose: z.string().trim().min(1).max(500).optional(),
  status: documentSectionStatusSchema,
  title: shortTextSchema,
}).strict();

export const documentSourceSchema = z.object({
  /** Clé citée dans le texte : `S1`, `S2`… */
  key: z.string().trim().regex(/^S[0-9]{1,3}$/),
  page: z.number().int().positive().optional(),
  title: shortTextSchema,
  url: z.url().optional(),
}).strict();

export const documentMetaSchema = z.object({
  author: shortTextSchema.optional(),
  /** Mention en couverture (« Confidentiel », « Usage interne »…). */
  classification: z.string().trim().min(1).max(60).optional(),
  date: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Libellé du type de document en couverture (« Rapport », « Note de synthèse »…). */
  documentLabel: z.string().trim().min(1).max(60).optional(),
  language: z.enum(['fr', 'en']).default('fr'),
  subtitle: z.string().trim().min(1).max(300).optional(),
  title: z.string().trim().min(1).max(160),
}).strict();

// --- Rapport (docx / pdf) ---------------------------------------------------

export const documentReportModelSchema = z.object({
  cover: z.enum(['full', 'band', 'minimal', 'none']).default('full'),
  kind: z.literal('report'),
  meta: documentMetaSchema,
  sections: z.array(documentSectionSchema).min(1).max(30),
  sources: z.array(documentSourceSchema).max(60).default([]),
  theme: documentThemeSchema,
  toc: z.boolean().default(true),
  version: z.literal(DOCUMENT_MODEL_VERSION),
}).strict();

// --- Présentation (pptx / pdf) ----------------------------------------------

export const documentSlideLayoutSchema = z.enum([
  'title',
  'section',
  'content',
  'two_column',
  'chart',
  'kpis',
  'quote',
  'closing',
]);

export const documentSlideSchema = z.object({
  /** Au plus 3 blocs : le rendu compose la slide selon le layout. */
  blocks: z.array(documentBlockSchema).max(3),
  id: idSchema,
  kicker: z.string().trim().min(1).max(60).optional(),
  layout: documentSlideLayoutSchema,
  notes: z.string().trim().min(1).max(2_000).optional(),
  status: documentSectionStatusSchema,
  subtitle: z.string().trim().min(1).max(200).optional(),
  title: shortTextSchema,
}).strict();

export const documentDeckModelSchema = z.object({
  kind: z.literal('deck'),
  meta: documentMetaSchema,
  slides: z.array(documentSlideSchema).min(1).max(40),
  sources: z.array(documentSourceSchema).max(60).default([]),
  theme: documentThemeSchema,
  version: z.literal(DOCUMENT_MODEL_VERSION),
}).strict();

// --- Classeur (xlsx) ----------------------------------------------------------

/** Cellule : texte, nombre ou vide (une colonne à formule porte `null`). */
export const documentCellSchema = z.union([
  z.string().max(400),
  z.number().finite(),
  z.null(),
]);

export const documentSheetColumnSchema = z.object({
  /**
   * Formule de colonne calculée ligne par ligne, en référençant d'autres colonnes
   * par leur en-tête entre crochets : `=[Prix unitaire]*[Quantité]`. Opérateurs
   * autorisés : + - * / ( ) et nombres. Le rendu la traduit en références de
   * cellules Excel et calcule aussi la valeur pour l'aperçu.
   */
  formula: z.string().trim().regex(/^=[\s\w[\]À-ÿ'’.,%+\-*/()]+$/).max(300).optional(),
  header: shortTextSchema,
  total: z.enum(['sum', 'average', 'min', 'max', 'count']).optional(),
  type: z.enum(['text', 'number', 'integer', 'currency', 'percent', 'date']).default('text'),
  width: z.number().int().min(6).max(80).optional(),
}).strict();

export const documentSheetTableSchema = z.object({
  columns: z.array(documentSheetColumnSchema).min(1).max(20),
  id: idSchema,
  /** Valeurs des colonnes sans formule ; `null` pour une colonne à formule. */
  rows: z.array(z.array(documentCellSchema)).max(500),
  title: shortTextSchema.optional(),
}).strict().refine(
  (table) => table.rows.every((row) => row.length === table.columns.length),
  { error: 'Chaque ligne doit avoir autant de cellules que de colonnes.', path: ['rows'] },
);

export const documentSheetChartSchema = z.object({
  categoryColumn: shortTextSchema,
  chartType: documentChartTypeSchema,
  tableId: idSchema,
  title: shortTextSchema,
  valueColumns: z.array(shortTextSchema).min(1).max(6),
}).strict();

export const documentSheetSchema = z.object({
  charts: z.array(documentSheetChartSchema).max(6).default([]),
  description: inlineTextSchema.optional(),
  id: idSchema,
  name: z.string().trim().min(1).max(31).regex(/^[^\\/?*[\]:]+$/),
  status: documentSectionStatusSchema,
  tables: z.array(documentSheetTableSchema).max(6),
}).strict();

export const documentWorkbookModelSchema = z.object({
  kind: z.literal('workbook'),
  meta: documentMetaSchema,
  sheets: z.array(documentSheetSchema).min(1).max(12),
  sources: z.array(documentSourceSchema).max(60).default([]),
  theme: documentThemeSchema,
  version: z.literal(DOCUMENT_MODEL_VERSION),
}).strict();

export const documentModelSchema = z.discriminatedUnion('kind', [
  documentReportModelSchema,
  documentDeckModelSchema,
  documentWorkbookModelSchema,
]);

export const documentModelKindSchema = z.enum(['report', 'deck', 'workbook']);

/** Formats de rendu accessibles pour chaque famille de modèle. */
export const DOCUMENT_MODEL_RENDER_FORMATS = {
  deck: ['pptx', 'pdf', 'html'],
  report: ['docx', 'pdf', 'html'],
  workbook: ['xlsx', 'html'],
} as const;

export const documentRenderFormatSchema = z.enum(['docx', 'pdf', 'pptx', 'xlsx', 'html']);

/** Famille de modèle déduite du format demandé à la création. */
export const DOCUMENT_KIND_TO_MODEL_KIND = {
  docx: 'report',
  pdf: 'report',
  pptx: 'deck',
  xlsx: 'workbook',
} as const satisfies Record<z.infer<typeof documentGenerationKindSchema>, z.infer<typeof documentModelKindSchema>>;

// --- Contrat interne backend Node -> service de rendu (backend-codegen) ------
// POST /render — réponse : octets du fichier (Content-Type du format), ou JSON
// d'erreur `{ error_code, message }` (DOCGEN_MODEL_INVALID / DOCGEN_RENDER_FAILED).

export const documentRenderRequestSchema = z.object({
  /** Logo d'entreprise optionnel (PNG/JPEG en base64), affiché en couverture et en-tête. */
  brandLogoBase64: z.string().max(2_000_000).optional(),
  format: documentRenderFormatSchema,
  /** `preview` (HTML seulement) : sections `pending` en squelette animé, blocs annotés `data-section-id` / `data-block-id`. */
  mode: z.enum(['final', 'preview']).default('final'),
  model: documentModelSchema,
}).strict();

// --- API frontend -------------------------------------------------------------

/** GET /api/chat/conversations/:id/generations/:generationId/model */
export const documentModelResponseSchema = z.object({
  engine: documentGenerationEngineSchema,
  generationId: z.uuid(),
  model: documentModelSchema,
  modelRevision: z.number().int().nonnegative(),
}).strict();

/** POST /api/chat/conversations/:id/generations/:generationId/exports */
export const documentExportRequestSchema = z.object({
  format: z.enum(['docx', 'pdf', 'pptx', 'xlsx']),
}).strict();

export const documentExportResponseSchema = z.object({
  contentType: z.string().trim().min(1),
  downloadUrl: z.url(),
  expiresIn: z.number().int().positive(),
  filename: z.string().trim().min(1),
}).strict();

/**
 * POST /api/chat/conversations/:id/generated-documents/:documentId/edits
 * Chaque requête crée une NOUVELLE version (immuable) à partir de `baseGenerationId`
 * et renvoie `documentGenerationAcceptedSchema`.
 * - `update_block` / `delete_block` / `move_block` / `delete_section` / `set_theme` :
 *   édition directe, sans LLM (nouvelle version rendue en < 2 s) ;
 * - `rewrite_block` : réécrit un bloc par le LLM (« reformule ce passage ») ;
 * - `rewrite_section` : régénère une section / slide / feuille par le LLM ;
 * - `insert_section` : ajoute une section générée par le LLM.
 * `sectionId` désigne indifféremment une section (rapport), une slide (deck) ou
 * une feuille (classeur) ; les opérations de bloc ne s'appliquent pas au classeur.
 */
export const documentEditOperationSchema = z.discriminatedUnion('op', [
  z.object({ block: documentBlockSchema, op: z.literal('update_block'), sectionId: idSchema }).strict(),
  z.object({ blockId: idSchema, op: z.literal('delete_block'), sectionId: idSchema }).strict(),
  z.object({ blockId: idSchema, op: z.literal('move_block'), position: z.number().int().nonnegative(), sectionId: idSchema }).strict(),
  z.object({ blockId: idSchema, instruction: z.string().trim().min(1).max(2_000), op: z.literal('rewrite_block'), sectionId: idSchema }).strict(),
  z.object({ instruction: z.string().trim().min(1).max(2_000).optional(), op: z.literal('rewrite_section'), sectionId: idSchema }).strict(),
  z.object({ op: z.literal('delete_section'), sectionId: idSchema }).strict(),
  z.object({
    afterSectionId: idSchema.nullable(),
    instruction: z.string().trim().min(1).max(2_000),
    op: z.literal('insert_section'),
    title: shortTextSchema,
  }).strict(),
  z.object({ op: z.literal('set_theme'), theme: documentThemeSchema }).strict(),
]);

export const documentEditRequestSchema = z.object({
  baseGenerationId: z.uuid(),
  operations: z.array(documentEditOperationSchema).min(1).max(20),
}).strict();

export type DocumentBlockDTO = z.infer<typeof documentBlockSchema>;
export type DocumentBlockTypeDTO = z.infer<typeof documentBlockTypeSchema>;
export type DocumentChartDTO = z.infer<typeof documentChartSchema>;
export type DocumentDeckModelDTO = z.infer<typeof documentDeckModelSchema>;
export type DocumentEditOperationDTO = z.infer<typeof documentEditOperationSchema>;
export type DocumentEditRequestDTO = z.infer<typeof documentEditRequestSchema>;
export type DocumentExportRequestDTO = z.infer<typeof documentExportRequestSchema>;
export type DocumentExportResponseDTO = z.infer<typeof documentExportResponseSchema>;
export type DocumentMetaDTO = z.infer<typeof documentMetaSchema>;
export type DocumentModelDTO = z.infer<typeof documentModelSchema>;
export type DocumentModelKindDTO = z.infer<typeof documentModelKindSchema>;
export type DocumentModelResponseDTO = z.infer<typeof documentModelResponseSchema>;
export type DocumentRenderFormatDTO = z.infer<typeof documentRenderFormatSchema>;
export type DocumentRenderRequestDTO = z.infer<typeof documentRenderRequestSchema>;
export type DocumentReportModelDTO = z.infer<typeof documentReportModelSchema>;
export type DocumentSectionDTO = z.infer<typeof documentSectionSchema>;
export type DocumentSheetDTO = z.infer<typeof documentSheetSchema>;
export type DocumentSlideDTO = z.infer<typeof documentSlideSchema>;
export type DocumentSourceDTO = z.infer<typeof documentSourceSchema>;
export type DocumentThemeDTO = z.infer<typeof documentThemeSchema>;
export type DocumentThemePresetDTO = z.infer<typeof documentThemePresetSchema>;
export type DocumentWorkbookModelDTO = z.infer<typeof documentWorkbookModelSchema>;
